import {AxiosError, AxiosResponse} from 'axios';
import {FieldProps, Form as FormikForm, FormikHelpers as FormikActions, FormikState, isNaN} from 'formik';
import * as React from 'react';
import {ReactNode} from 'react';
import {Link} from 'react-router-dom';
import * as Yup from 'yup';
import {Button} from '~/components/Button';
import {CheckboxInput, ICheckboxProps} from '~/components/Controls/Checkbox';
import {RadioInput, IRadioProps} from '~/components/Controls/Radio';
import {DateRangePickerInput, IDateRangePickerProps} from '~/components/Controls/DatePicker';
import {EditorInput} from '~/components/Controls/Editor';
import {FileInput, IFileProps} from '~/components/Controls/File';
import {IInputProps, Input, InputType} from '~/components/Controls/Input';
import {IMultiInputProps, MultiInput} from '~/components/Controls/MultiInput';
import {IMultiSelectProps, MultiSelectInput} from '~/components/Controls/Select';
import {ISelectProps, SelectInput} from '~/components/Controls/Select/SelectInput';
import {ITagsInputProps, TagsInput} from '~/components/Controls/Tags';
import {Textarea, ITextareaProps} from '~/components/Controls/Textarea';
import {getApiErrorMessage} from '~/lib/errors/apiError';
import {classByIntent, classWithIntent, Intent} from '~/lib/intent';
import style from './index.module.scss';

Yup.setLocale({
    mixed: {
        required: 'is required',
        oneOf: 'must be selected'
    },
    number: {
        min: param => `must be greater than or equal to ${param.min}`,
        max: param => `must be less than or equal to ${param.max}`
    },
});

export type IFormProps = React.FormHTMLAttributes<HTMLFormElement>;

export function Form(props: IFormProps) {
    let classes = [style.form, props.className];
    const childrenArray = React.Children.toArray(props.children);

    const titles: React.ReactNode[] = [];
    const content: React.ReactNode[] = [];
    const controls: React.ReactNode[] = [];

    childrenArray.forEach(child => {
        if (!React.isValidElement(child)) {
            content.push(child);
            return;
        }

        if (child.type === FormControls) {
            controls.push(child);
        } else if (child.type === FormTitle) {
            titles.push(child);
        } else {
            content.push(child);
        }
    });

    return <FormikForm {...props} className={classes.join(' ')}>
        {titles}
        <div className={style.formBody}>
            {content}
        </div>
        {controls}
    </FormikForm>;
}

type IFormTitleProps = React.HTMLAttributes<HTMLDivElement>;

export function FormTitle(props: IFormTitleProps) {
    let classes = [style.formTitle, props.className];
    return <div {...props} className={classes.join(' ')}/>;
}

interface IFormBlockProps extends React.HTMLAttributes<HTMLDivElement> {
    caption?: ReactNode;
    noBorder?: boolean;
    noMargin?: boolean;
}

export function FormRow({caption, noBorder, noMargin, ...props}: IFormBlockProps) {
    return <div {...props} className={[style.formRow, props.className].join(' ')}>
        {caption && <div className={[style.formRowCaption].join(' ')}>{caption}</div>}
        <div
            className={[style.formRowContent, noBorder && style.formRowNoBorder, noMargin && style.formRowNoMargin].join(' ')}>
            {props.children}
        </div>
    </div>;
}

interface IFormColumnProps extends React.HTMLAttributes<HTMLDivElement> {
    noBorder?: boolean;
    noMargin?: boolean;
}

export function FormColumn({noBorder, noMargin, ...props}: IFormColumnProps) {
    return <div {...props}
        className=
            {[style.formColumn, props.className, noBorder && style.formColumnNoBorder, noMargin && style.formColumnNoMargin].join(' ')}/>;
}

interface ILabelWithLinkProps {
    label: ReactNode;
    to: string;
    title?: string;
    ariaLabel?: string;
    target?: '_blank' | '_self' | '_parent' | '_top';
    onClick?: () => void;
}

export function LabelWithLink({label, to, title, ariaLabel, target = '_blank', onClick}: ILabelWithLinkProps) {
    const linkLabel = ariaLabel || title;
    return (
        <span className={style.labelWithLink}>
            {label}
            <Link
                to={to}
                className={style.labelLink}
                title={title}
                aria-label={linkLabel}
                target={target}
                rel={target === '_blank' ? 'noreferrer' : undefined}
                onClick={(event) => {
                    event.stopPropagation();
                    onClick?.();
                }}
            >
                <i className='fa fa-external-link' />
            </Link>
        </span>
    );
}

export interface IFormFieldProps extends FieldProps {
    className?: string;
    inline?: boolean;
    label?: ReactNode;
    required?: boolean;
    disabled?: boolean;
    intent?: Intent;
    children?: ReactNode;
    help?: ReactNode;
    note?: ReactNode;
    hideError?: boolean;
    onLabelClick?: () => void;
}

// TODO: don't want to fiddle with types on this rn
export const nestedErrors = ({form, field}: IFormFieldProps) => {
    let parts = field.name.split('.');
    let error: unknown;
    if (parts.length === 1) {
        error = form.errors[field.name];
    } else if (parts.length === 2 && form.errors[parts[0]] !== undefined) {
        error = form.errors[parts[0]][parts[1]];
    } else if (parts.length === 3) {
        const arrayFieldName = parts[0];
        const index = parts[1];
        const fieldName = parts[2];
        const arrayErrors = form.errors[arrayFieldName];
        if (arrayErrors !== undefined && arrayErrors[index] !== undefined) {
            error = arrayErrors[index][fieldName];
        }
    }
    if (typeof error === 'string') {
        return error;
    }
    return '';
};

type IFormControlsProps = React.HTMLAttributes<HTMLDivElement>;

export function FormControls(props: IFormControlsProps) {
    let classes = [style.formControls, props.className];
    return <div {...props} className={classes.join(' ')}/>;
}

export interface IInputFieldProps<T> {
    id?: string;
    value?: T;
    intent?: Intent;
    onChange?: (value: T) => void;
    disabled?: boolean;
}

export interface IFieldProps<T> extends IFormFieldProps {
    options: T;
}

export const onChangeField = <T extends any>({form, field}: IFormFieldProps, value: T, type?: number) => {
    let newValue: any;
    try {
        switch (type) {
            case InputType.FLOAT:
                newValue = Number.parseFloat(value as any);
                break;
            case InputType.INTEGER:
                newValue = Number.parseInt(value as any, 10);
                break;
            default:
                newValue = value;
        }
    } finally {
        if (isNaN(newValue)) {
            newValue = '';
        }
        form.setFieldValue(field.name, newValue);
    }
};

export const hasErrors =
    <T extends any>(formState: FormikState<T>, fieldNames: (keyof T)[] | any): boolean =>
        formState.submitCount > 0 && !!fieldNames.find(fieldName => !!formState.errors[fieldName]);

export const hasError = ({form, field}: IFormFieldProps) => {
    let parts = field.name.split('.');
    if (parts.length === 1) {
        return form.errors[field.name] && (form.touched[field.name] || form.submitCount > 0);
    } else if (parts.length === 2 && form.errors[parts[0]] !== undefined) {
        return form.errors[parts[0]][parts[1]] && form.submitCount > 0;
    } else if (parts.length === 3) {
        const arrayFieldName = parts[0];
        const index = parts[1];
        const fieldName = parts[2];
        const arrayErrors = form.errors[arrayFieldName];
        if (arrayErrors !== undefined && arrayErrors[index] !== undefined) {
            return form.submitCount > 0 && arrayErrors[index][fieldName];
        }
    }
};

export interface IButtonInputProps {
    onClick: () => void;
    intent: Intent;
    icon: JSX.Element;
}

interface IFieldMessageProps extends React.HTMLAttributes<HTMLDivElement> {
    intent?: Intent;
}

export const IntentText = ({intent = Intent.DEFAULT, ...props}: IFieldMessageProps) => <div {...props}
    className={classWithIntent([
        style.helpMessage,
        props.className
    ].join(' '), intent)}
/>;

interface INoteMessageProps {
    children: ReactNode;
}

export const NoteMessage = ({children}: INoteMessageProps) => <div className={style.fieldNote}>{children}</div>;

export const onSubmitError =
    <T extends any, E extends any>(formikActions: FormikActions<T>, callback?: (response: AxiosResponse<T>) => E) =>
        (error: AxiosError) => {
            formikActions.setSubmitting(false);
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            callback
                ? callback(error.response)
                : formikActions.setStatus(getApiErrorMessage(error));
        };

export function FormField({
    label,
    required,
    note,
    help,
    intent,
    className,
    children,
    field,
    form,
    meta,
    hideError,
    onLabelClick,
}: IFormFieldProps) {
    let classes = [style.field, classByIntent(intent), className];
    if (label) {
        classes.push(style.fieldWithLabel);
    }
    return <div className={classes.join(' ')}>
        {label && (
            <label
                htmlFor={field.name}
                className={style.fieldLabel}
                onClick={onLabelClick}
            >
                {label}
                {required && <span className={style.requiredStar}>*</span>}
            </label>
        )}
        <div className={style.fieldElement}>
            {children}
            {!hideError && hasError({field, form, meta}) &&
                <IntentText intent={Intent.DANGER} children={nestedErrors({field, form, meta})}/>}
            {help && <IntentText intent={Intent.PRIMARY}>{help}</IntentText>}
            {note && <NoteMessage children={note}/>}
        </div>
    </div>;
}

export const DateInputField = (props: IFieldProps<IDateRangePickerProps>) => <FormField {...props}>
    <DateRangePickerInput {...props.options} dateRange={props.field.value}
        onChange={dateRange => onChangeField(props, dateRange)}/>
</FormField>;

export const InputField = (props: IFieldProps<IInputProps> & { value?: string}) => {
    return <FormField {...props}>
    <Input {...props.options} id={props.field.name} value={props.field.value ?? props.value}
        intent={hasError(props) && Intent.DANGER}
        onChange={value => onChangeField(props, value, props.options.type)}
    />
</FormField>};

export const TextareaField = (props: IFieldProps<ITextareaProps>) => <FormField {...props}>
    <Textarea {...props.options} id={props.field.name} value={props.field.value}
        intent={hasError(props) && Intent.DANGER}
        onChange={value => onChangeField(props, value)}
    />
</FormField>

export const MultiInputField = <T extends any>(props: IFieldProps<IMultiInputProps>) => <FormField {...props}>
    <MultiInput {...props.options}
        intent={hasError(props) && Intent.DANGER}
        value={props.field.value} onChange={value => onChangeField(props, value)}
    />
</FormField>;

export const ButtonField = (props: IFieldProps<IButtonInputProps>) => <FormField {...props}>
    <Button responsive
        intent={props.options.intent}
        onClick={props.options.onClick}
        leftIcon={props.options.icon}/>
</FormField>;

export const FileField = (props: IFieldProps<IFileProps>) => <FormField {...props}>
    <FileInput {...props.options} id={props.field.name} value={props.field.value}
        intent={hasError(props) && Intent.DANGER}
        onChange={value => onChangeField(props, value)}
    />
</FormField>;

export const CheckboxField = (props: IFieldProps<ICheckboxProps>) => {
    const { className, ...rest } = props;
    const disabled = props.options?.disabled;
    const fieldClassName = [className, style.fieldCheckbox].filter(Boolean).join(' ');

    const toggle = () => {
        if (disabled) {
            return;
        }
        onChangeField(props, !props.field.value as any);
    };

    return (
        <FormField
            {...rest}
            className={fieldClassName}
            onLabelClick={toggle}
        >
            <CheckboxInput
                disabled={disabled}
                value={props.field.value}
                intent={hasError(props) && Intent.DANGER}
                onChange={value => disabled || onChangeField(props, value)}
            />
        </FormField>
    );
};

export const RadioField = (props: IFieldProps<IRadioProps>) => (
  <FormField {...props}>
    <RadioInput
      value={props.field.value}
      optionValue={props.options.optionValue}
      label={props.options.label}
      intent={hasError(props) && Intent.DANGER}
      onChange={value => onChangeField(props, value)}
      disabled={props.options.disabled}
    />
  </FormField>
);

export const SelectField = <T extends any>(props: IFieldProps<ISelectProps<T>>) => <FormField {...props}>
    <SelectInput {...props.options}
        intent={hasError(props) && Intent.DANGER}
        value={props.field.value} onChange={value => onChangeField(props, value)}
    />
</FormField>;

export const TagsField = <T extends any>(props: IFieldProps<ITagsInputProps>) => {
    const [focused, setFocused] = React.useState(false);
    const [pendingValue, setPendingValue] = React.useState('');
    const shouldShowEnterHint = !props.options.dropdown && !props.options.disabled;
    const showHint = shouldShowEnterHint && focused && pendingValue.trim().length > 0;

    return <FormField {...props}>
        <div className={style.tagFieldWrapper}>
            <TagsInput {...props.options}
                onFocusChange={setFocused}
                onPendingChange={setPendingValue}
                intent={hasError(props) && Intent.DANGER} dropdown={props.options.dropdown}
                value={props.field.value} onChange={value => onChangeField(props, value)}
            />
            {showHint &&
                <span className={style.tagHint}>Press Enter to add</span>}
        </div>
    </FormField>;
};

export const MultiSelectField = <T extends any>(props: IFieldProps<IMultiSelectProps<T>>) => <FormField {...props}>
    <MultiSelectInput {...props.options}
        intent={hasError(props) && Intent.DANGER}
        value={props.field.value} onChange={value => onChangeField(props, value)}
    />
</FormField>;

export const EditorField = (props: IFieldProps<{}>) => <FormField {...props}>
    <EditorInput {...props.options} value={props.field.value} onChange={value => onChangeField(props, value)}/>
</FormField>;
